'use client';

import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import { useRouter } from 'next/router';
import { Package, Handshake, Users, Wrench } from 'lucide-react';
import { isFinnishPage } from '../../../lib/blogPaths';

// Unified navy/slate scale – same family, clearly distinct steps
const COLLABORATION_COLORS = [
  { main: '#0f172a', badgeBg: '#f1f5f9', badgeText: '#334155' },
  { main: '#1e293b', badgeBg: '#e2e8f0', badgeText: '#334155' },
  { main: '#334155', badgeBg: '#cbd5e1', badgeText: '#1e293b' },
  { main: '#475569', badgeBg: '#cbd5e1', badgeText: '#1e293b' },
];

const MODELS_EN = [
  { icon: Package, title: 'Turnkey Solution', tagline: 'Hands-off delivery, start to finish', description: 'For clients who value having work completed autonomously, on time, and within schedule—from planning to the final product or service launch and ongoing maintenance.' },
  { icon: Handshake, title: 'Close Collaboration', tagline: 'Co-create and learn together', description: "For clients who value close collaboration and active involvement in the project's progress. The Identio team works closely with the client, ensuring the seamless transfer of knowledge and expertise to the client organization." },
  { icon: Users, title: 'Integrating into an Existing Team', tagline: 'Add expertise where you need it', description: 'For clients who already have a product or service in development and need assistance in a specific area, such as software development or UX design.' },
  { icon: Wrench, title: 'Maintenance & Minor Dev', tagline: 'Keep it running, keep it growing', description: 'For clients with a launched product or service that requires maintenance, ongoing support, or minor development work.' },
];

const MODELS_FI = [
  { icon: Package, title: 'Avaimet käteen', tagline: 'Koko prosessi hoidettu alusta loppuun', description: 'Asiakkaille, jotka arvostavat, että työt hoituvat itseohjautuvasti ajallaan ja aikataulussa aina suunnittelusta lopullisen tuotteen tai palvelun julkaisuun ja ylläpitoon.' },
  { icon: Handshake, title: 'Läheinen yhteistyö', tagline: 'Yhdessä suunnittelemme ja opimme', description: 'Asiakkaille, joille läheinen yhteistyö ja osallistuminen projektin etenemiseen on tärkeää. Idention tiimi työskentelee tiiviissä yhteistyössä asiakkaan kanssa varmistaen samalla tiedon ja osaamisen siirtymisen asiakas-organisaatioon.' },
  { icon: Users, title: 'Osaksi olemassa olevaa tiimiä', tagline: 'Osaaminen sinne missä tarvitaan', description: 'Asiakkaille, joilla on jo kehitteillä oleva tuote tai palvelu, jonka kehittämiseen tarvitaan apua tarkemmin määritellyltä osa-alueelta, kuten ohjelmistokehityksestä tai UX-suunnittelusta.' },
  { icon: Wrench, title: 'Ylläpito ja pienkehitys', tagline: 'Pidä järjestelmä käynnissä ja kehityksen alla', description: 'Asiakkaille, joilla on jo julkaistu tuote tai palvelu, joka kaipaa ylläpitoa, jatkuvaa tukea tai pienimuotoisempaa kehitystyötä.' },
];

export function SoftwareDevCollaborationSection() {
  const ref = useRef(null);
  const router = useRouter();
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const isFinnish = isFinnishPage(router?.pathname);
  const models = isFinnish ? MODELS_FI : MODELS_EN;
  const heading = isFinnish ? 'Erilaiset yhteistyömallit' : 'Collaboration models';
  const subtitle = isFinnish ? 'Valitse miten haluat työskennellä kanssamme—kokonaisvastuusta tiimin vahvistamiseen.' : 'Choose how you want to work with us—from full ownership to augmenting your team.';

  return (
    <section ref={ref} className="section-rhythm bg-gradient-to-b from-[#f8fafc] to-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="heading-2 mb-4">{heading}</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {models.map((item, index) => {
            const Icon = item.icon;
            const color = COLLABORATION_COLORS[index];
            return (
              <motion.article
                key={item.title}
                initial={{ opacity: 0, y: 28 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative flex flex-col p-6 lg:p-8 rounded-2xl border border-gray-200 bg-white overflow-hidden hover:shadow-[0_16px_48px_-12px_rgba(0,0,0,0.12)] hover:border-gray-300 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <span
                    className="flex items-center justify-center w-12 h-12 rounded-xl shrink-0 shadow-sm text-white"
                    style={{ backgroundColor: color.main }}
                    aria-hidden
                  >
                    <Icon className="w-6 h-6" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <span
                      className="inline-block px-2.5 py-1 rounded-md text-xs font-medium mb-3"
                      style={{ backgroundColor: color.badgeBg, color: color.badgeText }}
                    >
                      {item.tagline}
                    </span>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-[15px] text-gray-600 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
